import { useMemo, useState } from 'react';
import type { ImportResult, Stakeholder } from '@integratio/core';
import { Search } from 'lucide-react';

const PAGE_SIZE = 25;

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <label className="flex min-w-[190px] flex-1 flex-col gap-1">
      <span className="eyebrow">{label}</span>
      <select
        className="rounded-xl border border-brand-line bg-white px-3 py-2 text-sm text-brand-ink focus:border-brand-red focus:outline-none"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Todos</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ImportedDataTable({ result }: { result: ImportResult }): JSX.Element {
  const [search, setSearch] = useState('');
  const [context, setContext] = useState('');
  const [priority, setPriority] = useState('');
  const [behavior, setBehavior] = useState('');
  const [page, setPage] = useState(0);

  const options = useMemo(
    () => ({
      context: unique(result.stakeholders.map((item) => item.contextoAmbiente)),
      priority: unique(result.stakeholders.map((item) => item.prioridade)),
      behavior: unique(result.stakeholders.map((item) => item.poderInteresses)),
    }),
    [result.stakeholders],
  );

  const warningRows = useMemo(() => new Set(result.warnings.map((warning) => warning.rowNumber)), [result.warnings]);

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR');
    return result.stakeholders.filter((item) => {
      if (context && item.contextoAmbiente !== context) return false;
      if (priority && item.prioridade !== priority) return false;
      if (behavior && item.poderInteresses !== behavior) return false;
      if (!term) return true;
      return (
        item.nome.toLocaleLowerCase('pt-BR').includes(term) ||
        item.empresaInstituicao.toLocaleLowerCase('pt-BR').includes(term) ||
        item.cargo.toLocaleLowerCase('pt-BR').includes(term)
      );
    });
  }, [result.stakeholders, search, context, priority, behavior]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const rows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const numeric = (value: number | null): string => (value === null ? '—' : String(value));

  return (
    <section className="card overflow-hidden">
      <div className="flex flex-col gap-4 border-b border-brand-line p-6">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex min-w-[240px] flex-1 flex-col gap-1">
            <span className="eyebrow">Buscar stakeholder</span>
            <span className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate" aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Nome, empresa ou cargo"
                className="w-full rounded-xl border border-brand-line bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-red focus:outline-none"
              />
            </span>
          </label>
          <Select
            label="Contexto do Ambiente"
            value={context}
            options={options.context}
            onChange={(value) => {
              setContext(value);
              setPage(0);
            }}
          />
          <Select
            label="Prioridade"
            value={priority}
            options={options.priority}
            onChange={(value) => {
              setPriority(value);
              setPage(0);
            }}
          />
          <Select
            label="Postura e Comportamento"
            value={behavior}
            options={options.behavior}
            onChange={(value) => {
              setBehavior(value);
              setPage(0);
            }}
          />
        </div>
        <p className="text-xs text-brand-slate">
          Exibindo {rows.length} de {filtered.length} registros filtrados · {result.stakeholders.length} stakeholders
          importados
        </p>
      </div>

      <div className="max-h-[540px] overflow-auto">
        <table className="w-full min-w-[1180px] border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-brand-mist text-left">
            <tr className="text-[11px] uppercase tracking-wider text-brand-slate">
              <th className="px-4 py-3 font-semibold">Nº</th>
              <th className="px-4 py-3 font-semibold">Stakeholder</th>
              <th className="px-4 py-3 font-semibold">Esfera</th>
              <th className="px-4 py-3 font-semibold">Categoria</th>
              <th className="px-4 py-3 font-semibold">Empresa/Instituição</th>
              <th className="px-4 py-3 font-semibold">Cargo</th>
              <th className="px-4 py-3 text-right font-semibold">Posic.</th>
              <th className="px-4 py-3 text-right font-semibold">Poder</th>
              <th className="px-4 py-3 font-semibold">Contexto</th>
              <th className="px-4 py-3 text-right font-semibold">Ação</th>
              <th className="px-4 py-3 text-right font-semibold">Interesses</th>
              <th className="px-4 py-3 font-semibold">Postura</th>
              <th className="px-4 py-3 font-semibold">Prioridade</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item: Stakeholder) => (
              <tr
                key={item.id}
                className={[
                  'border-t border-brand-line/70 align-top',
                  warningRows.has(item.rowNumber) ? 'bg-state-attention/5' : 'bg-white',
                ].join(' ')}
              >
                <td className="px-4 py-3 tabular-nums text-brand-slate">{item.numero || '—'}</td>
                <td className="px-4 py-3 font-medium text-brand-ink">{item.nome}</td>
                <td className="px-4 py-3 text-brand-slate">{item.esfera || '—'}</td>
                <td className="px-4 py-3 text-brand-slate">{item.categoria || '—'}</td>
                <td className="px-4 py-3 text-brand-slate">{item.empresaInstituicao || '—'}</td>
                <td className="px-4 py-3 text-brand-slate">{item.cargo || '—'}</td>
                <td className="px-4 py-3 text-right tabular-nums">{numeric(item.valorPosicionamento)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{numeric(item.valorPoder)}</td>
                <td className="px-4 py-3">{item.contextoAmbiente || '—'}</td>
                <td className="px-4 py-3 text-right tabular-nums">{numeric(item.valorAcao)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{numeric(item.valorInteresses)}</td>
                <td className="px-4 py-3">{item.poderInteresses || '—'}</td>
                <td className="px-4 py-3 text-brand-slate">{item.prioridade || '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={13} className="px-4 py-10 text-center text-sm text-brand-slate">
                  Nenhum registro corresponde aos filtros aplicados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between gap-4 border-t border-brand-line px-6 py-4 text-sm">
          <button type="button" className="btn-secondary" onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage === 0}>
            Anterior
          </button>
          <span className="text-brand-slate">
            Página {safePage + 1} de {pageCount}
          </span>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
            disabled={safePage >= pageCount - 1}
          >
            Próxima
          </button>
        </div>
      )}
    </section>
  );
}
